import React, { useState } from 'react';
import { Zap, Heart, Leaf, HeartPulse, ArrowRight, Dumbbell, Pill, Sparkles, Check, Flame, Brain, ShieldPlus, Activity, Plus } from 'lucide-react';

type CategoryId = 'SPORTS' | 'VITAMINS' | 'AYURVEDA' | 'FUNCTIONAL';

interface Product {
  name: string;
  desc: string;
  tag?: string;
}

interface CategoryData {
  id: CategoryId;
  name: string;
  icon: React.ReactNode;
  description: string;
  colorClass: string;
  bgClass: string;
  products: Product[];
}

interface ProductsPageProps {
  onNavigate?: (page: 'home' | 'blog' | 'contact' | 'resources' | 'products', sectionId?: string) => void;
}

const ProductsPage: React.FC<ProductsPageProps> = ({ onNavigate }) => {
  const [activeCategory, setActiveCategory] = useState<CategoryId>('SPORTS');

  const handleContactClick = (e: React.MouseEvent) => {
      e.preventDefault();
      if (onNavigate) {
          onNavigate('contact');
          window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
          window.location.href = '/contact';
      }
  };

  const categories: Record<CategoryId, CategoryData> = {
    SPORTS: {
      id: 'SPORTS',
      name: 'Sports Nutrition',
      icon: <Dumbbell className="w-5 h-5" />,
      description: 'High-performance formulas designed for athletes, bodybuilders, and fitness enthusiasts.',
      colorClass: 'text-orange-600',
      bgClass: 'bg-orange-50',
      products: [
        { name: "Whey Protein Isolate", desc: "90% Protein, minimal carbs/fats. Ultra-filtered for rapid absorption.", tag: "Best Seller" },
        { name: "Performance Whey Blend", desc: "Cost-effective blend of Concentrate & Isolate. Creamy texture profile.", tag: "High Margin" },
        { name: "Pre-Workout Ignition", desc: "Beta-Alanine, Citrulline, Caffeine blend. No crash formula.", tag: "Trending" },
        { name: "BCAA 2:1:1 + Electrolytes", desc: "Instantized for easy mixing. Added hydration matrix.", tag: "Essential" },
        { name: "Micronized Creatine", desc: "100% pure Creatine Monohydrate. 200 mesh size.", tag: "Staple" },
        { name: "Mass Gainer Complex", desc: "High calorie carb-protein matrix with digestive enzymes.", tag: "Volume Mover" },
        { name: "EAA Full Spectrum", desc: "All 9 Essential Amino Acids for maximum recovery.", tag: "Premium" },
        { name: "L-Glutamine Recovery", desc: "Pure fermented L-Glutamine for gut health and recovery.", tag: "Staple" },
        { name: "Casein Protein", desc: "Slow-release micellar casein for night-time muscle recovery.", tag: "Night Recovery" },
        { name: "L-Carnitine Liquid 3000", desc: "High potency liquid metabolic booster for fat loss.", tag: "Fat Loss" },
        { name: "Beta-Alanine Pure", desc: "Raw endurance powder to delay muscle fatigue.", tag: "Endurance" },
        { name: "Citrulline Malate 2:1", desc: "Nitric oxide booster for massive pumps and blood flow.", tag: "Pump" },
        { name: "ZMA Night Complex", desc: "Zinc, Magnesium, and B6 blend for sleep and testosterone support.", tag: "Sleep" },
        { name: "Electrolyte Hydration", desc: "Sugar-free hydration powder with key minerals.", tag: "Hydration" },
        { name: "Plant Protein (Pea+Rice)", desc: "Complete amino profile vegan protein. Smooth texture.", tag: "Vegan" },
        { name: "Testosterone Booster", desc: "Natural herbal blend with Tribulus and Fenugreek.", tag: "Men's Health" },
        { name: "Thermogenic Fat Burner", desc: "High-stimulant capsule for rapid calorie burning.", tag: "Weight Mgmt" },
        { name: "Joint Support (Athletes)", desc: "Glucosamine + Chondroitin + MSM heavy duty formula.", tag: "Protection" },
        { name: "Isotonic Energy Drink", desc: "Instant energy carb-electrolyte matrix for runners.", tag: "Energy" },
        { name: "Carb Loader", desc: "Pure Maltodextrin or Cluster Dextrin for intra-workout.", tag: "Performance" }
      ]
    },
    VITAMINS: {
      id: 'VITAMINS',
      name: 'Vitamins & Minerals',
      icon: <Pill className="w-5 h-5" />,
      description: 'Essential daily nutrition to bridge the gap in modern diets.',
      colorClass: 'text-blue-600',
      bgClass: 'bg-blue-50',
      products: [
        { name: "Daily Multivitamin", desc: "Complete spectrum of 24 vitamins & minerals. RDA compliant.", tag: "Daily Use" },
        { name: "Omega-3 Fish Oil", desc: "Double strength EPA/DHA. Enteric coated softgels (no fishy burps).", tag: "Heart Health" },
        { name: "Vitamin D3 + K2", desc: "Optimal bone health and immunity support. Oil-based softgel.", tag: "Immunity" },
        { name: "Magnesium Glycinate", desc: "High absorption form for sleep and muscle relaxation.", tag: "Trending" },
        { name: "Zinc + Vitamin C", desc: "Powerful immunity boosting combination.", tag: "Seasonal" },
        { name: "Iron + Folic Acid", desc: "Gentle iron formula for energy and blood health.", tag: "Women's Health" },
        { name: "Calcium Citrate", desc: "Highly bioavailable calcium with Vitamin D3.", tag: "Bone Health" },
        { name: "B-Complex Super", desc: "High potency B-vitamins for energy metabolism.", tag: "Energy" },
        { name: "Vitamin C 1000mg", desc: "Chewable antioxidant support tablets.", tag: "Antioxidant" },
        { name: "Vitamin E 400 IU", desc: "Natural source d-alpha tocopherol for skin health.", tag: "Skin" },
        { name: "Biotin 10,000mcg", desc: "Maximum strength for hair growth and nail strength.", tag: "Beauty" },
        { name: "Methyl B12", desc: "Sublingual tablets for nerve health and energy.", tag: "Brain" },
        { name: "Chromium Picolinate", desc: "Supports healthy blood sugar metabolism.", tag: "Metabolic" },
        { name: "Selenium 200mcg", desc: "Essential mineral for thyroid and immune function.", tag: "Thyroid" },
        { name: "CoQ10 100mg", desc: "Ubiquinone form for heart health and cellular energy.", tag: "Heart" },
        { name: "Prenatal Multivitamin", desc: "Formulated for pregnancy with Iron and DHA.", tag: "Maternal" },
        { name: "Kids Multivitamin Gummies", desc: "Delicious fruit flavors with essential nutrients.", tag: "Kids" },
        { name: "Vitamin A (Retinol)", desc: "Essential for vision and immune health.", tag: "Vision" },
        { name: "Potassium Citrate", desc: "Electrolyte balance and muscle function.", tag: "Muscle" },
        { name: "Copper Glycinate", desc: "Trace mineral for connective tissue health.", tag: "Joints" }
      ]
    },
    AYURVEDA: {
      id: 'AYURVEDA',
      name: 'Ayurvedic & Herbal',
      icon: <Leaf className="w-5 h-5" />,
      description: 'Ancient wisdom standardized for the modern consumer.',
      colorClass: 'text-green-600',
      bgClass: 'bg-green-50',
      products: [
        { name: "KSM-66 Ashwagandha", desc: "Clinical strength stress relief and testosterone support.", tag: "Global Hit" },
        { name: "Shilajit Resin", desc: "Purified Himalayan Shilajit with >60% Fulvic Acid. Lab tested.", tag: "High Ticket" },
        { name: "Turmeric Curcumin", desc: "95% Curcuminoids with Black Pepper extract for absorption.", tag: "Anti-Inflammatory" },
        { name: "Triphala Gut Cleanse", desc: "Gentle daily detox and digestive support blend.", tag: "Gut Health" },
        { name: "Liver Detox", desc: "Milk Thistle + Dandelion + Kutki complex.", tag: "Detox" },
        { name: "Immunity Kadha", desc: "Traditional blend of Holy Basil, Cinnamon, and Ginger.", tag: "Traditional" },
        { name: "Safed Musli Extract", desc: "Standardized extract for vitality and performance.", tag: "Men's Health" },
        { name: "Brahmi Focus", desc: "Memory and cognitive support herb.", tag: "Brain" },
        { name: "Giloy (Guduchi)", desc: "Potent immunomodulator and fever reducer.", tag: "Immunity" },
        { name: "Neem Skin Purifier", desc: "Blood purifier for clear and healthy skin.", tag: "Skin" },
        { name: "Karela Jamun Juice", desc: "Traditional diabetic care support.", tag: "Diabetes" },
        { name: "Amla Vitamin C", desc: "Natural Vitamin C source for immunity and hair.", tag: "Antioxidant" },
        { name: "Gokshura (Tribulus)", desc: "Kidney health and vitality support.", tag: "Vitality" },
        { name: "Shatavari", desc: "Women's hormonal balance and wellness.", tag: "Women" },
        { name: "Moringa Leaf Powder", desc: "Nutrient-dense superfood powder.", tag: "Superfood" },
        { name: "Arjuna Heart Care", desc: "Cardiovascular health support bark extract.", tag: "Heart" },
        { name: "Manjistha", desc: "Lymphatic support and blood cleansing.", tag: "Detox" },
        { name: "Tulsi Drops", desc: "Concentrated holy basil extract for respiratory health.", tag: "Respiratory" },
        { name: "Aloe Vera Juice", desc: "Soothing digestive aid and skin hydrator.", tag: "Digestion" },
        { name: "Shankhpushpi", desc: "Brain tonic for concentration and memory.", tag: "Memory" }
      ]
    },
    FUNCTIONAL: {
      id: 'FUNCTIONAL',
      name: 'Functional Wellness',
      icon: <HeartPulse className="w-5 h-5" />,
      description: 'Targeted solutions for specific health concerns and lifestyle needs.',
      colorClass: 'text-purple-600',
      bgClass: 'bg-purple-50',
      products: [
        { name: "Hair, Skin & Nails", desc: "Biotin, Collagen, and Keratin booster blend.", tag: "Beauty" },
        { name: "Probiotic Complex", desc: "30 Billion CFU multi-strain formula for gut health.", tag: "Gut Health" },
        { name: "Melatonin Sleep Aid", desc: "Non-habit forming sleep support with Valerian Root.", tag: "Sleep" },
        { name: "Collagen Peptides", desc: "Hydrolyzed bovine or marine collagen for skin elasticity.", tag: "Anti-Aging" },
        { name: "Apple Cider Vinegar", desc: "With 'The Mother'. Available in effervescent or gummy.", tag: "Weight Mgmt" },
        { name: "Green Coffee Bean", desc: "Standardized Chlorogenic Acid for metabolism.", tag: "Fat Burner" },
        { name: "Eye Health Formula", desc: "Lutein and Zeaxanthin for blue light protection.", tag: "Digital Age" },
        { name: "Joint Support", desc: "Glucosamine, Chondroitin, and MSM blend.", tag: "Senior Health" },
        { name: "L-Theanine", desc: "Amino acid for calm focus and relaxation without drowsiness.", tag: "Stress" },
        { name: "5-HTP", desc: "Serotonin precursor for mood balance.", tag: "Mood" },
        { name: "Glutathione", desc: "Master antioxidant for skin brightening and detox.", tag: "Skin" },
        { name: "Resveratrol", desc: "Anti-aging antioxidant from grape skin.", tag: "Longevity" },
        { name: "Hyaluronic Acid", desc: "Oral supplement for skin hydration and joints.", tag: "Beauty" },
        { name: "Cranberry Extract", desc: "Urinary tract health support.", tag: "Women's Health" },
        { name: "Milk Thistle", desc: "Liver protection and regeneration.", tag: "Liver" },
        { name: "Digestive Enzymes", desc: "Amylase, Protease, Lipase blend for better digestion.", tag: "Digestion" },
        { name: "Psyllium Husk", desc: "Natural fiber for regularity and heart health.", tag: "Fiber" },
        { name: "Mushroom Blend", desc: "Reishi, Lion's Mane, and Cordyceps complex.", tag: "Nootropic" },
        { name: "Keto BHB Salts", desc: "Exogenous ketones for ketosis support.", tag: "Keto" },
        { name: "Garcinia Cambogia", desc: "Appetite suppression and weight management.", tag: "Diet" }
      ]
    }
  };

  const currentCategory = categories[activeCategory];

  return (
    <div className="bg-white min-h-screen pb-24">
      {/* Header with Gradient Background */}
      <section className="relative py-24 md:py-32 lg:py-40 overflow-hidden bg-black">
        {/* Background Gradient Layer */}
        <div className="absolute inset-0 z-0">
           {/* Dark gradient base */}
           <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-black to-orange-950"></div>
           
           {/* Decorative abstract glows */}
           <div className="absolute top-0 right-0 w-[400px] h-[400px] md:w-[600px] md:h-[600px] bg-orange-600/20 rounded-full blur-[80px] md:blur-[120px] -mr-20 -mt-20 pointer-events-none mix-blend-screen"></div>
           <div className="absolute bottom-0 left-0 w-[400px] h-[400px] md:w-[600px] md:h-[600px] bg-orange-900/20 rounded-full blur-[80px] md:blur-[100px] -ml-20 -mb-20 pointer-events-none mix-blend-screen"></div>
           
           {/* Faint noise overlay for texture */}
           <div className="absolute inset-0 opacity-[0.15]" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opacity='0.5'/%3E%3C/svg%3E")` }}></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
             {/* Badge - Glassmorphism */}
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/20 mb-8 shadow-2xl">
                <span className="text-white text-xs font-bold uppercase tracking-widest drop-shadow-sm">
                    Product Portfolio
                </span>
            </div>
            
            <h1 className="text-3xl sm:text-4xl md:text-6xl font-extrabold text-white mb-6 tracking-tight drop-shadow-lg leading-tight">
                Private Label <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-orange-700">Supplement Products</span>
            </h1>
            <p className="text-lg md:text-xl text-gray-300 max-w-2xl mx-auto font-medium drop-shadow-md">
                Choose from our library of 500+ market-ready formulas.
            </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-10 relative z-20">
        
        {/* Category Tabs - Single Row Scrollable - Modern White/Orange Style */}
        <div className="flex justify-center mb-12">
          <div className="bg-white p-2 rounded-2xl shadow-xl border border-gray-100 inline-flex flex-nowrap overflow-x-auto no-scrollbar gap-3 max-w-full">
            {Object.values(categories).map((cat) => (
                <button
                    key={cat.id}
                    onClick={() => setActiveCategory(cat.id)}
                    className={`px-4 py-3 md:px-6 md:py-4 rounded-xl text-sm font-bold transition-all flex items-center whitespace-nowrap flex-shrink-0 border ${
                        activeCategory === cat.id
                        ? 'bg-orange-500 border-orange-500 text-white shadow-lg shadow-orange-500/30'
                        : 'bg-gray-50 border-transparent text-gray-500 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                >
                    <span className="mr-2">{cat.icon}</span>
                    <span>{cat.name}</span>
                </button>
            ))}
          </div>
        </div>

        {/* CONTENT AREA */}
        <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
            {/* Category Header */}
            <div className="text-center max-w-3xl mx-auto mb-12">
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">{currentCategory.name}</h2>
                <p className="text-gray-600 text-base md:text-lg">
                    {currentCategory.description}
                </p>
            </div>

            {/* Products Grid */}
            <div className={`rounded-3xl border border-gray-100 shadow-lg overflow-hidden bg-white`}>
                <div className={`${currentCategory.bgClass} p-4 md:p-6 border-b border-gray-100 flex items-center justify-between`}>
                    <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-gray-500">
                        <Sparkles className="w-4 h-4" /> Market Ready Catalog
                    </div>
                    <button 
                        onClick={handleContactClick}
                        className="hidden md:block px-6 py-2 bg-white border border-gray-200 text-gray-900 rounded-lg font-bold text-sm hover:bg-black hover:text-white transition-colors"
                    >
                        Request Spec Sheets
                    </button>
                </div>
                
                <div className="p-4 md:p-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                    {currentCategory.products.map((item, i) => (
                        <div key={i} className="group p-5 rounded-2xl border border-gray-100 hover:border-gray-300 hover:shadow-xl transition-all duration-300 bg-gray-50/30 hover:bg-white flex flex-col">
                            <div className="flex justify-between items-start mb-3">
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${currentCategory.bgClass} ${currentCategory.colorClass}`}>
                                    {currentCategory.icon}
                                </div>
                                {item.tag && (
                                    <span className="text-[10px] font-bold uppercase tracking-wider bg-gray-200 text-gray-600 px-2 py-1 rounded">
                                        {item.tag}
                                    </span>
                                )}
                            </div>
                            
                            <h4 className="font-bold text-gray-900 mb-2 text-lg leading-tight group-hover:text-orange-600 transition-colors">
                                {item.name}
                            </h4>
                            <p className="text-sm text-gray-500 mb-6 leading-relaxed flex-grow">
                                {item.desc}
                            </p>
                            
                            <div className="mt-auto border-t border-gray-100 pt-4 flex items-center justify-between">
                                <span className="text-xs font-semibold text-gray-400">MOQ: Low</span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>

        {/* Bottom Formats Strip */}
        <div className="mt-16 md:mt-24 grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
             <div className="p-4 md:p-6 bg-gray-50 rounded-2xl border border-gray-100">
                 <div className="font-bold text-gray-900 mb-1">Powders</div>
                 <div className="text-xs text-gray-500">Jars, Pouches, Sachets</div>
             </div>
             <div className="p-4 md:p-6 bg-gray-50 rounded-2xl border border-gray-100">
                 <div className="font-bold text-gray-900 mb-1">Capsules</div>
                 <div className="text-xs text-gray-500">Veg, Gelatin, HPMC</div>
             </div>
             <div className="p-4 md:p-6 bg-gray-50 rounded-2xl border border-gray-100">
                 <div className="font-bold text-gray-900 mb-1">Tablets</div>
                 <div className="text-xs text-gray-500">Coated, Chewable, Effervescent</div>
             </div>
             <div className="p-4 md:p-6 bg-gray-50 rounded-2xl border border-gray-100">
                 <div className="font-bold text-gray-900 mb-1">Liquids</div>
                 <div className="text-xs text-gray-500">Drops, Shots, Syrups</div>
             </div>
        </div>

      </div>
      
      {/* CTA */}
      <section className="max-w-4xl mx-auto px-4 mt-24 text-center">
         <h2 className="text-2xl font-bold text-gray-900 mb-6">Don't see what you're looking for?</h2>
         <p className="text-gray-600 mb-8 max-w-xl mx-auto">We have 400+ other formulas in our full catalog, or we can develop a custom formula just for you.</p>
         <a 
            href="/contact" 
            onClick={handleContactClick}
            className="inline-flex items-center px-8 py-4 border border-transparent text-lg font-bold rounded-xl text-white bg-black hover:bg-orange-500 hover:text-black transition-all shadow-lg cursor-pointer"
         >
            Talk to an Expert <ArrowRight className="ml-2 w-5 h-5" />
         </a>
      </section>
    </div>
  );
};

export default ProductsPage;